if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      /*
        Quick add used to fetch the whole product page (~950KB on this store) and
        throw away everything except <product-info>. Asking for just that section
        is ~75% smaller.

        The section id must be the TEMPLATE-scoped one, e.g.
        template--28316396880056__main. Do NOT use section_id=main-product: it
        returns 200 and looks plausible, but JSON-template sections render with
        their default schema blocks, so the response has no add-to-cart button,
        no variant picker, no price and no PartBot fitment block.

        JS cannot know that id up front, so the first quick add falls back to the
        full page, reads the id off <product-info data-section>, and caches it for
        later taps. Every fetch is validated before use, and anything unexpected
        falls back to the full page, so the worst case is exactly the old
        behaviour.
      */
      static SECTION_CACHE_KEY = 'quickAddProductSectionId';

      static getCachedSectionId() {
        try {
          return window.localStorage.getItem(QuickAddModal.SECTION_CACHE_KEY);
        } catch (e) {
          return null;
        }
      }

      static setCachedSectionId(id) {
        try {
          if (id) window.localStorage.setItem(QuickAddModal.SECTION_CACHE_KEY, id);
        } catch (e) {}
      }

      static isUsable(productElement) {
        return !!(productElement && productElement.querySelector('[name="add"]'));
      }

      fetchProductElement(url) {
        return fetch(url)
          .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
          .then((responseText) => new DOMParser().parseFromString(responseText, 'text/html').querySelector('product-info'));
      }

      loadProductElement(productUrl) {
        const cached = QuickAddModal.getCachedSectionId();

        const fromFullPage = () =>
          this.fetchProductElement(productUrl).then((productElement) => {
            if (productElement && productElement.dataset.section) {
              QuickAddModal.setCachedSectionId(productElement.dataset.section);
            }
            return productElement;
          });

        if (!cached) return fromFullPage();

        const separator = productUrl.indexOf('?') === -1 ? '?' : '&';
        const sectionUrl = `${productUrl}${separator}section_id=${encodeURIComponent(cached)}`;

        return this.fetchProductElement(sectionUrl)
          .catch(() => null)
          .then((productElement) => (QuickAddModal.isUsable(productElement) ? productElement : fromFullPage()));
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        const productUrl = opener.getAttribute('data-product-url');

        this.loadProductElement(productUrl)
          .then((productElement) => {
            if (!QuickAddModal.isUsable(productElement)) {
              // Nothing safe to show. Send them to the product page rather than
              // opening a modal they cannot buy from.
              window.location.href = productUrl;
              return;
            }

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .catch(() => {
            window.location.href = productUrl;
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
/*
  <quick-add-panel> — the compact quick add used on product cards.

  Replaces the old "open a stripped-down product page in a modal" flow for
  products that only need a variant chosen. The panel is rendered with the card
  (see snippets/quick-add-panel.liquid) so opening it costs no network request.

  Desktop: a popover anchored over the bottom of its own card.
  Mobile:  a bottom sheet.

  Add to cart deliberately goes through the theme's existing <product-form>, so
  cart drawer rendering, error handling and analytics behave exactly as they do
  everywhere else. This element only picks the variant and closes itself once
  the item is in the cart.
*/
if (!customElements.get('quick-add-panel')) {
  customElements.define(
    'quick-add-panel',
    class QuickAddPanel extends HTMLElement {
      static openPanel = null;
      static MOBILE_QUERY = '(max-width: 749px)';
      // Title, price, one row of options and the buy button, roughly.
      static MIN_HEIGHT = 300;
      static STICKY_SELECTOR = '.header-group, .header-wrapper, sticky-header, header';

      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.opener = this.querySelector('[data-qap-open]');
        if (this.hasAttribute('data-qap-lazy') && !this.querySelector('.qap__dialog')) {
          this.setupLazy();
          return;
        }
        this.setup();
      }

      /*
        Cards print only the opener. The dialog is fetched the first time the
        shopper shows intent (hover, focus or touch start, which usually lands
        it before the click does) and inserted into this element, after which
        the panel runs exactly as if it had been printed with the card.
      */
      setupLazy() {
        const prefetch = () => this.loadDialog().catch(() => {});
        ['pointerenter', 'focusin', 'touchstart'].forEach((type) =>
          this.opener.addEventListener(type, prefetch, { once: true, passive: true })
        );

        this.onLazyClick = async (event) => {
          event.preventDefault();
          if (this.lazyBusy) return;
          this.lazyBusy = true;

          const spinner = document.createElement('span');
          spinner.className = 'qap__spinner';
          spinner.setAttribute('aria-hidden', 'true');
          this.opener.setAttribute('aria-busy', 'true');
          this.opener.appendChild(spinner);

          try {
            await this.loadDialog();
          } catch (e) {
            // No panel to show; the product page still sells the product.
            window.location.href = this.dataset.productUrl;
            return;
          } finally {
            spinner.remove();
            this.opener.removeAttribute('aria-busy');
            this.lazyBusy = false;
          }

          this.opener.removeEventListener('click', this.onLazyClick);
          this.setup();
          if (!this.variants.length) {
            window.location.href = this.dataset.productUrl;
            return;
          }
          this.open();
        };
        this.opener.addEventListener('click', this.onLazyClick);
      }

      loadDialog() {
        if (!this.dialogRequest) {
          const url = new URL(this.dataset.productUrl, window.location.origin);
          url.searchParams.set('section_id', 'quick-add-panel-fragment');

          this.dialogRequest = fetch(url.toString())
            .then((response) => {
              if (!response.ok) throw new Error(`Quick add panel: ${response.status}`);
              return response.text();
            })
            .then((html) => {
              if (this.querySelector('.qap__dialog')) return;
              const template = document.createElement('template');
              template.innerHTML = html.split('__QAP_SECTION__').join(this.dataset.sectionId || '');
              const parts = template.content.querySelectorAll(
                '[data-qap-scrim], .qap__dialog, script[data-qap-variants]'
              );
              if (parts.length !== 3) throw new Error('Quick add panel: incomplete fragment');
              parts.forEach((part) => this.appendChild(part));
            });
          // Let a failed request be retried on the next attempt.
          this.dialogRequest.catch(() => {
            this.dialogRequest = null;
          });
        }
        return this.dialogRequest;
      }

      setup() {
        this.dialog = this.querySelector('[data-qap-dialog], .qap__dialog');
        this.scrim = this.querySelector('[data-qap-scrim]');
        this.priceEl = this.querySelector('[data-qap-price]');
        this.variantInput = this.querySelector('[data-qap-variant-id]');
        this.submit = this.querySelector('[data-qap-submit]');
        this.submitText = this.submit ? this.submit.querySelector('span') : null;
        this.cardWrapper = this.closest('.card-wrapper');
        this.optionEls = Array.from(this.querySelectorAll('[data-qap-option]'));
        this.optionCount = parseInt(this.dataset.optionCount, 10) || this.optionEls.length;

        this.defaultPriceHTML = this.priceEl ? this.priceEl.innerHTML : '';
        this.addToCartText = this.submitText ? this.submitText.textContent.trim() : 'Add to cart';

        this.variants = this.readVariants();
        if (!this.variants.length) {
          // Nothing to choose from. Let the button behave as a link to the product.
          this.opener.addEventListener('click', () => {
            window.location.href = this.dataset.productUrl;
          });
          return;
        }

        this.selection = new Array(this.optionCount).fill(null);

        this.opener.addEventListener('click', this.open.bind(this));
        if (this.scrim) this.scrim.addEventListener('click', () => this.close());
        this.querySelectorAll('[data-qap-close]').forEach((btn) =>
          btn.addEventListener('click', () => this.close())
        );

        this.querySelectorAll('[data-qap-value]').forEach((input) =>
          input.addEventListener('change', () => this.choose(input))
        );
        this.querySelectorAll('[data-qap-select]').forEach((select) =>
          select.addEventListener('change', () => this.chooseFromSelect(select))
        );

        // Single-value options come out of Liquid already checked/selected.
        this.querySelectorAll('[data-qap-value]:checked').forEach((input) => this.choose(input, true));
        this.optionEls.forEach((optionEl) => {
          const select = optionEl.querySelector('[data-qap-select]');
          if (select && select.value) this.chooseFromSelect(select, true);
        });

        this.form = this.querySelector('form');
        if (this.form) {
          this.form.addEventListener(
            'submit',
            (event) => {
              if (!this.variantInput.value) {
                event.preventDefault();
                event.stopImmediatePropagation();
              }
            },
            true
          );
        }

        // Crossing the mobile/desktop breakpoint changes where the sheet has to
        // live, so close rather than leave a portalled panel behind.
        const mq = window.matchMedia(QuickAddPanel.MOBILE_QUERY);
        const onBreakpoint = () => this.close(true);
        if (mq.addEventListener) mq.addEventListener('change', onBreakpoint);

        this.onKeydown = this.onKeydown.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);

        // The form posts through <product-form>; close once the cart confirms.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (!this.isOpen) return;
            const addedId = String(event.productVariantId || '');
            if (addedId && addedId === String(this.variantInput.value)) this.close(true);
          });
        }

        this.refresh();
      }

      readVariants() {
        const script = this.querySelector('[data-qap-variants]');
        if (!script) return [];
        try {
          const parsed = JSON.parse(script.textContent);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          return [];
        }
      }

      get isOpen() {
        return this.classList.contains('qap--open');
      }

      // Panels in the cart drawer (data-qap-sheet) always open as a sheet over
      // the drawer: inside the drawer's scrolling card row the desktop popover
      // would be clipped.
      get isMobile() {
        return this.hasAttribute('data-qap-sheet') || window.matchMedia(QuickAddPanel.MOBILE_QUERY).matches;
      }

      /*
        The card sits inside a transformed ancestor, which makes position: fixed
        resolve against that ancestor instead of the viewport - the mobile sheet
        would be pinned to the bottom of its own card. Moving the sheet to
        <body> for as long as it is open is the only reliable way out. Listeners
        and the <product-form> instance survive the move.
      */
      portalOut() {
        if (this.portalled) return;
        this.portalAnchor = document.createComment('qap');
        this.dialog.before(this.portalAnchor);
        const sheet = this.hasAttribute('data-qap-sheet');
        this.dialog.classList.toggle('qap__dialog--drawer', sheet);
        // data-qap-center (product page carousel): a centred popup on desktop
        // instead of a sheet docked where the cart drawer would be.
        this.dialog.classList.toggle('qap__dialog--center', this.hasAttribute('data-qap-center'));
        this.scrim.classList.toggle('qap__scrim--drawer', sheet);
        if (sheet) {
          // Desktop: match the cart drawer's width and right edge. Outside the
          // drawer (product page carousel) the drawer is closed and measures 0,
          // so fall back to its CSS width.
          const inner = document.querySelector('cart-drawer .drawer__inner');
          const drawerWidth = inner && inner.offsetWidth;
          this.dialog.style.width = window.innerWidth >= 750 ? (drawerWidth ? `${drawerWidth}px` : '50rem') : '';
        }
        document.body.appendChild(this.scrim);
        document.body.appendChild(this.dialog);
        this.portalled = true;
      }

      portalHome() {
        if (!this.portalled) return;
        this.portalAnchor.before(this.scrim);
        this.portalAnchor.before(this.dialog);
        this.portalAnchor.remove();
        this.portalAnchor = null;
        this.portalled = false;
      }

      owns(node) {
        return this.contains(node) || this.dialog.contains(node) || this.scrim.contains(node);
      }

      open(event) {
        if (event) event.preventDefault();
        if (this.isOpen) return this.close();

        if (QuickAddPanel.openPanel && QuickAddPanel.openPanel !== this) QuickAddPanel.openPanel.close();
        QuickAddPanel.openPanel = this;

        if (this.isMobile) this.portalOut();

        this.dialog.hidden = false;
        if (this.scrim) this.scrim.hidden = false;
        this.classList.add('qap--open');
        this.dialog.classList.add('qap__dialog--open');
        this.scrim.classList.add('qap__scrim--open');
        this.opener.setAttribute('aria-expanded', 'true');
        if (this.cardWrapper) this.cardWrapper.classList.add('card-wrapper--qap-open');
        if (this.isMobile) document.body.classList.add('qap-scroll-locked');

        document.addEventListener('keydown', this.onKeydown);
        document.addEventListener('click', this.onDocumentClick, true);

        this.fitToViewport();

        requestAnimationFrame(() => {
          this.classList.add('qap--visible');
          this.dialog.classList.add('qap__dialog--visible');
          this.dialog.focus({ preventScroll: true });
        });
      }

      /*
        The desktop panel only ever shows over its own card: bottom edge pinned
        to its button, growing upwards.

        Two clamps keep it whole. Pull the bottom edge up when the button itself
        sits below the fold, or the buy button ends up off the bottom of the
        screen; then cap the height so the top cannot run past the top of the
        screen. The options list is the only part that scrolls, so the title and
        the buy button always stay put.

        Earlier attempts flipped the panel below the button, and nudged the page
        to make room. The flip looked detached - floating over the row beneath
        with its own card's button hidden behind it - and the scroll nudge is not
        reliable, so every misfit silently became a flip.
      */
      /* The panel now sits below the sticky header rather than over it, so the
         header would hide its top. Find how far down the page actually starts. */
      topLimit(margin) {
        let limit = margin;
        document.querySelectorAll(QuickAddPanel.STICKY_SELECTOR).forEach((el) => {
          const position = getComputedStyle(el).position;
          if (position !== 'sticky' && position !== 'fixed') return;
          const rect = el.getBoundingClientRect();
          if (rect.height && rect.top <= margin && rect.bottom + margin > limit) {
            limit = rect.bottom + margin;
          }
        });
        return limit;
      }

      fitToViewport() {
        this.dialog.style.maxHeight = '';
        this.dialog.style.bottom = '';
        this.dialog.classList.remove('qap__dialog--compact');
        if (this.isMobile) return;

        const margin = 16;
        const opener = this.opener.getBoundingClientRect();

        const bottomEdge = Math.min(opener.bottom, window.innerHeight - margin);
        this.dialog.style.bottom = `${Math.round(opener.bottom - bottomEdge)}px`;

        /*
          Prefer to stay inside the card, so a tall product does not spill up
          over the row above. But short cards - the ones in the product page
          carousels - give so little room that the options list collapses to a
          scrollbar, so never go below what the panel needs to stay usable.
          The viewport is the only hard limit.
        */
        const card = this.cardWrapper ? this.cardWrapper.getBoundingClientRect() : null;
        const top = this.topLimit(margin);
        const viewportRoom = bottomEdge - top;
        const cardRoom = card ? bottomEdge - Math.max(top, card.top) : viewportRoom;
        const roomAbove = Math.min(viewportRoom, Math.max(cardRoom, QuickAddPanel.MIN_HEIGHT));

        if (this.dialog.offsetHeight > roomAbove) {
          this.dialog.style.maxHeight = `${Math.round(roomAbove)}px`;
        }

        // When the room above is genuinely tight, drop the secondary link so the
        // space goes to the options rather than to chrome.
        this.dialog.classList.toggle('qap__dialog--compact', roomAbove < QuickAddPanel.MIN_HEIGHT);
      }

      close(preventFocus = false) {
        if (!this.isOpen) return;

        this.classList.remove('qap--open', 'qap--visible');
        this.dialog.classList.remove('qap__dialog--open', 'qap__dialog--visible');
        this.scrim.classList.remove('qap__scrim--open');
        this.dialog.hidden = true;
        if (this.scrim) this.scrim.hidden = true;
        this.opener.setAttribute('aria-expanded', 'false');
        if (this.cardWrapper) this.cardWrapper.classList.remove('card-wrapper--qap-open');
        document.body.classList.remove('qap-scroll-locked');

        document.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('click', this.onDocumentClick, true);

        this.portalHome();

        if (QuickAddPanel.openPanel === this) QuickAddPanel.openPanel = null;
        if (!preventFocus) this.opener.focus({ preventScroll: true });
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          event.stopPropagation();
          this.close();
        }
      }

      onDocumentClick(event) {
        if (!this.owns(event.target)) this.close(true);
      }

      choose(input, silent = false) {
        const optionEl = input.closest('[data-qap-option]');
        const index = parseInt(optionEl.dataset.optionIndex, 10) - 1;
        this.selection[index] = input.value || null;

        const label = optionEl.querySelector('[data-qap-selected-label]');
        if (label) label.textContent = this.selection[index] ? `: ${this.selection[index]}` : '';

        if (!silent) this.refresh();
      }

      chooseFromSelect(select, silent = false) {
        const optionEl = select.closest('[data-qap-option]');
        const index = parseInt(optionEl.dataset.optionIndex, 10) - 1;
        this.selection[index] = select.value || null;
        select.classList.toggle('is-selected', !!select.value);
        if (!silent) this.refresh();
      }

      /* A value is offered when some in-stock variant carries it alongside every
         other option the shopper has already chosen. */
      isValueOffered(index, value) {
        return this.variants.some((variant) => {
          if (!variant.a || variant.o[index] !== value) return false;
          return this.selection.every(
            (selected, i) => i === index || selected === null || variant.o[i] === selected
          );
        });
      }

      matchedVariant() {
        if (this.selection.some((value) => value === null)) return null;
        return (
          this.variants.find((variant) =>
            this.selection.every((value, i) => variant.o[i] === value)
          ) || null
        );
      }

      refresh() {
        this.optionEls.forEach((optionEl) => {
          const index = parseInt(optionEl.dataset.optionIndex, 10) - 1;

          // The theme styles unavailable options off these two class names.
          optionEl.querySelectorAll('[data-qap-value]').forEach((el) => {
            const offered = this.isValueOffered(index, el.value);
            el.classList.toggle('disabled', !offered);
            el.classList.toggle('visually-disabled', !offered);
          });

          optionEl.querySelectorAll('[data-qap-select] option').forEach((el) => {
            if (!el.value) return;
            if (el.dataset.qapLabel === undefined) el.dataset.qapLabel = el.textContent.trim();
            const offered = this.isValueOffered(index, el.value);
            el.classList.toggle('is-unavailable', !offered);
            el.textContent = offered
              ? el.dataset.qapLabel
              : `${el.dataset.qapLabel} - ${(window.variantStrings && window.variantStrings.soldOut) || 'Sold out'}`;
          });
        });

        const variant = this.matchedVariant();
        const complete = !this.selection.some((value) => value === null);

        if (variant) {
          this.priceEl.innerHTML = variant.c
            ? `${variant.p} <s class="qap__price-was">${variant.c}</s>`
            : variant.p;
        } else {
          this.priceEl.innerHTML = this.defaultPriceHTML;
        }

        this.variantInput.value = variant && variant.a ? variant.id : '';

        const buyable = !!(variant && variant.a);
        this.submit.disabled = !buyable;
        this.submit.classList.toggle('qap__submit--waiting', !complete);
        if (this.submitText) {
          this.submitText.textContent =
            complete && !buyable
              ? (window.variantStrings && window.variantStrings.soldOut) || 'Sold out'
              : this.addToCartText;
        }
      }
    }
  );
}
